import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogHeader,
    DialogBody,
    DialogFooter,
    DialogClose,
} from "@/components/podkit/dialog/Dialog";
import { TrackLocations } from "@/hooks/use-segment";
import { useId, useState, useCallback, type FC } from "react";
import { WorkflowEditForm } from "./AutomationEditForm";
import { useUpdateWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import {
    convertFormStepsToWorkflowStep,
    convertFormTriggersToProtobuf,
    type WorkflowEditFormData,
} from "./automation-edit-form-data";
import { DEFAULT_WORKFLOW_LIMITS, workflowName } from "@/utils/automations";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { WorkflowAction, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";

type AutomationEditDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    workflow?: PlainWorkflow;
};

const AutomationEditDialog: FC<AutomationEditDialogProps> = ({ open, workflow, onOpenChange }) => {
    const formId = useId();
    const [isFormDisabled, setIsFormDisabled] = useState(true);
    const { mutateAsync: updateWorkflow, isPending: isUpdatingWorkflow } = useUpdateWorkflow();
    const { toast } = useToast();

    const onEditFormSubmit = useCallback(
        async ({ name, description, executor, steps, triggers, limits }: WorkflowEditFormData) => {
            if (!workflow) return;

            try {
                await updateWorkflow({
                    workflowId: workflow.id,
                    name,
                    description,
                    executor: executor,
                    action: new WorkflowAction({
                        limits: new WorkflowAction_Limits({
                            maxParallel: limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                            maxTotal: limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                        }),
                        steps: convertFormStepsToWorkflowStep(steps),
                    }),
                    triggers: triggers ? convertFormTriggersToProtobuf(triggers) : undefined,
                });
                onOpenChange(false);
            } catch (error) {
                toast({
                    title: "Failed to update automation",
                    description: formatError(error),
                });
            }
        },
        [workflow, updateWorkflow, onOpenChange, toast],
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-screen-md" data-track-location={TrackLocations.AutomationEditDialog}>
                <DialogHeader>
                    <DialogTitle>Edit Automation {`'${workflowName(workflow)}'`}</DialogTitle>
                </DialogHeader>
                <DialogBody className="p-0">
                    {workflow ? (
                        <WorkflowEditForm
                            workflow={workflow}
                            formName={formId}
                            onDisabledChange={setIsFormDisabled}
                            onSubmit={onEditFormSubmit}
                        />
                    ) : null}
                </DialogBody>
                <DialogFooter className="justify-end">
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isUpdatingWorkflow || isFormDisabled}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        form={formId}
                        variant="primary"
                        type="submit"
                        data-tracking-id="save-changes-automation-edit-dialog"
                        loading={isUpdatingWorkflow}
                        disabled={isFormDisabled}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AutomationEditDialog;
